import { makeAutoObservable, runInAction } from 'mobx'
import { api } from '@/shared/api/endpoints'
import type {
  AdminAccountResponse, AdminChartResponse, AdminOverviewResponse, FiatCurrency,
  TransactionResponse, WithdrawalAdminResponse,
} from '@/shared/api/types'
import { explain } from '@/shared/lib/errors'
import { MIN_PASSWORD } from '@/stores/SettingsStore'
import { Resource } from '@/stores/lib/Resource'
import type { UiStore } from '@/stores/UiStore'

const PAGE = 20

export type AccountDialog =
  | { kind: 'none' }
  | { kind: 'create' }
  | { kind: 'edit'; account: AdminAccountResponse }
  | { kind: 'remove'; account: AdminAccountResponse }
  | { kind: 'reset2fa'; account: AdminAccountResponse }

export interface AccountDraft {
  email: string
  password: string
  role: 'user' | 'admin'
  commission: string
  enabled: boolean
  paymentLinks: boolean
  clientPaysFee: boolean
  /** Числовой id Telegram для входа без пароля. Пусто — не привязан. */
  telegram: string
}

const EMPTY: AccountDraft = {
  email: '', password: '', role: 'user', commission: '5',
  enabled: true, paymentLinks: true, clientPaysFee: false, telegram: '',
}

/**
 * Админка.
 *
 * Здесь два разных по цене действия. Правка аккаунта обратима.
 * Одобрение вывода — нет: после него заявка уходит подписанту
 * и в сеть. Поэтому очередь выводов живёт отдельным ресурсом
 * и перечитывается после каждого решения: одобрить дважды одну
 * заявку из-за устаревшего списка нельзя.
 */
export class AdminStore {
  readonly overview: Resource<AdminOverviewResponse>
  readonly chart: Resource<AdminChartResponse>
  readonly accounts: Resource<{
    items: AdminAccountResponse[]; total: number; fiat: FiatCurrency
  }>
  readonly pending: Resource<{ items: WithdrawalAdminResponse[]; total: number }>
  /**
   * Аккаунты для фильтра графика («Все аккаунты» в макете). Отдельный
   * ресурс, а не `accounts`: тот листается и фильтруется поиском,
   * а селекту нужен весь список разом.
   *
   * Верхняя граница API — 100. Шлюз со ста мерчантами в селекте
   * увидит первые сто; когда это случится, нужен селект с поиском,
   * а не большее число здесь.
   */
  readonly accountOptions: Resource<AdminAccountResponse[]>
  /** Карточка одного мерчанта и его операции. */
  readonly card: Resource<AdminAccountResponse>
  readonly cardTx: Resource<{ items: TransactionResponse[]; total: number }>
  cardId = ''
  cardTxOffset = 0

  chartDays = 7
  /** null — все аккаунты. */
  chartAccountId: string | null = null
  search = ''
  offset = 0
  pendingOffset = 0
  /** Фиатная валюта админских сводок. Отдельная от кабинетной. */
  view: FiatCurrency = 'usd'

  dialog: AccountDialog = { kind: 'none' }
  draft: AccountDraft = EMPTY
  errors: Partial<Record<keyof AccountDraft, string>> = {}
  busy = false
  /** Заявка, по которой сейчас идёт решение, — чтобы крутить именно её кнопку. */
  decidingId: string | null = null

  private searchTimer: ReturnType<typeof setTimeout> | null = null
  private readonly ui: UiStore

  constructor(ui: UiStore) {
    this.ui = ui
    this.overview = new Resource(() => api.admin.overview(this.view))
    this.chart = new Resource(() =>
      api.admin.commissionChart(this.chartDays, this.view, this.chartAccountId ?? undefined))
    this.accountOptions = new Resource(async () => {
      const page = await api.admin.accounts({ limit: 100, offset: 0, currency_view: this.view })
      return page.items
    })
    this.accounts = new Resource(async () => {
      const page = await api.admin.accounts({
        limit: PAGE, offset: this.offset, currency_view: this.view,
        ...(this.search.trim() ? { search: this.search.trim() } : {}),
      })
      // Валюту берём из ОТВЕТА, а не из this.view: пока запрос летел,
      // админ мог переключить показ, и подписать доллары рублёвым
      // знаком — ровно тот случай, когда цифру читают неверно.
      return { items: page.items, total: page.total, fiat: page.fiat_currency ?? 'usd' }
    })
    this.pending = new Resource(async () => {
      // Пагинация, а не «первые 50»: очередь на одобрение — это заявки,
      // по которым мерчант ждёт свои деньги, и молча обрезанный хвост
      // означает, что часть из них не увидят вообще.
      const page = await api.admin.pendingWithdrawals({
        limit: PAGE, offset: this.pendingOffset,
      })
      return { items: page.items, total: page.total }
    })
    this.card = new Resource(() => api.admin.account(this.cardId))
    this.cardTx = new Resource(async () => {
      const p = await api.admin.accountTransactions(this.cardId,
        { limit: PAGE, offset: this.cardTxOffset })
      return { items: p.items, total: p.total }
    })
    makeAutoObservable<this, 'ui' | 'searchTimer'>(this, { ui: false, searchTimer: false })
  }

  get limit(): number {
    return PAGE
  }

  async loadDashboard(): Promise<void> {
    await Promise.all([this.overview.load(), this.chart.load(), this.accountOptions.load()])
  }

  setChartDays(days: number): void {
    this.chartDays = days
    void this.chart.load()
  }

  setChartAccount(id: string | null): void {
    if (this.chartAccountId === id) return
    this.chartAccountId = id
    void this.chart.load()
  }

  /** Смена валюты перезагружает и сводку, и график — иначе они разъедутся. */
  setView(view: FiatCurrency): void {
    if (this.view === view) return
    this.view = view
    void this.overview.load()
    void this.chart.load()
  }

  goPending(offset: number): void {
    this.pendingOffset = offset
    void this.pending.load()
  }

  setSearch(value: string): void {
    this.search = value
    this.offset = 0
    // Пауза перед запросом: без неё каждая буква — отдельный запрос
    // к списку аккаунтов, и на бекенде это выглядит как перебор.
    if (this.searchTimer !== null) clearTimeout(this.searchTimer)
    this.searchTimer = setTimeout(() => { void this.accounts.load() }, 350)
  }

  goToOffset(offset: number): void {
    this.offset = offset
    void this.accounts.load()
  }

  /** Открыть карточку мерчанта. Сбрасывает страницу операций. */
  openCard(id: string): void {
    if (this.cardId !== id) {
      this.cardId = id
      this.cardTxOffset = 0
    }
    void this.card.load()
    void this.cardTx.load()
  }

  goCardTx(offset: number): void {
    this.cardTxOffset = offset
    void this.cardTx.load()
  }

  // ---------------- аккаунты ----------------

  openCreate(): void {
    this.dialog = { kind: 'create' }
    this.draft = { ...EMPTY }
    this.errors = {}
  }

  openEdit(account: AdminAccountResponse): void {
    this.dialog = { kind: 'edit', account }
    this.draft = {
      email: account.email,
      password: '',
      role: account.role,
      commission: account.commission_percent,
      enabled: account.enabled,
      paymentLinks: account.payment_links_enabled,
      clientPaysFee: account.client_pays_network_fee,
      telegram: account.telegram_id ? String(account.telegram_id) : '',
    }
    this.errors = {}
  }

  openRemove(account: AdminAccountResponse): void {
    this.dialog = { kind: 'remove', account }
  }

  /**
   * Быстрое включение и отключение аккаунта прямо из списка.
   *
   * Отдельным действием, а не через диалог правки: в макете в строке
   * стоит переключатель. Шаг обратим — заблокированный аккаунт
   * включается тем же тумблером, — поэтому подтверждения нет.
   */
  async setEnabled(account: AdminAccountResponse, enabled: boolean): Promise<void> {
    if (this.busy) return
    this.busy = true
    try {
      await api.admin.updateAccount(account.id, { enabled })
      await this.accounts.load()
      this.ui.success(enabled ? 'Аккаунт включён' : 'Аккаунт заблокирован')
    } catch (cause) {
      this.ui.failure(explain(cause))
    } finally {
      runInAction(() => { this.busy = false })
    }
  }

  openReset2fa(account: AdminAccountResponse): void {
    this.dialog = { kind: 'reset2fa', account }
  }

  close(): void {
    if (this.busy) return
    this.dialog = { kind: 'none' }
    this.errors = {}
  }

  patch(part: Partial<AccountDraft>): void {
    this.draft = { ...this.draft, ...part }
    for (const key of Object.keys(part) as (keyof AccountDraft)[]) {
      if (this.errors[key]) {
        const rest = { ...this.errors }
        delete rest[key]
        this.errors = rest
      }
    }
  }

  private validate(creating: boolean): boolean {
    const errors: Partial<Record<keyof AccountDraft, string>> = {}
    const email = this.draft.email.trim()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = 'Похоже, это не почта'
    if (creating && this.draft.password.length < MIN_PASSWORD) {
      errors.password = `Не короче ${MIN_PASSWORD} символов`
    }
    const telegram = this.draft.telegram.trim()
    if (telegram !== '' && !/^\d{1,20}$/.test(telegram)) {
      errors.telegram = 'Только цифры'
    }
    if (!creating && this.draft.password !== '' && this.draft.password.length < MIN_PASSWORD) {
      errors.password = `Не короче ${MIN_PASSWORD} символов`
    }
    const commission = this.draft.commission.trim().replace(',', '.')
    if (!/^\d{1,2}(\.\d{1,2})?$/.test(commission) || Number(commission) > 100) {
      // Комиссия — процент с двумя знаками, здесь number безопасен.
      errors.commission = 'Процент от 0 до 100'
    }
    this.errors = errors
    return Object.keys(errors).length === 0
  }

  async submit(): Promise<void> {
    const dialog = this.dialog
    if (this.busy || (dialog.kind !== 'create' && dialog.kind !== 'edit')) return
    if (!this.validate(dialog.kind === 'create')) return

    const commission = this.draft.commission.trim().replace(',', '.')
    this.busy = true
    try {
      if (dialog.kind === 'create') {
        await api.admin.createAccount({
          email: this.draft.email.trim(),
          password: this.draft.password,
          role: this.draft.role,
          commission_percent: commission,
          enabled: this.draft.enabled,
          payment_links_enabled: this.draft.paymentLinks,
          client_pays_network_fee: this.draft.clientPaysFee,
        })
        this.ui.success('Аккаунт создан')
      } else {
        await api.admin.updateAccount(dialog.account.id, {
          email: this.draft.email.trim(),
          role: this.draft.role,
          commission_percent: commission,
          enabled: this.draft.enabled,
          payment_links_enabled: this.draft.paymentLinks,
          client_pays_network_fee: this.draft.clientPaysFee,
          // Пустое поле пароля означает «не менять», а не «стереть».
          ...(this.draft.password === '' ? {} : { password: this.draft.password }),
          // Telegram: пусто — отвязать (0), число — привязать. Шлём только
          // если поменялось: иначе каждое сохранение трогало бы привязку.
          ...(telegramChanged(dialog.account, this.draft.telegram)
            ? { telegram_id: this.draft.telegram.trim() === '' ? 0 : Number(this.draft.telegram.trim()) }
            : {}),
        })
        this.ui.success('Аккаунт изменён')
      }
      await this.accounts.load()
      // Диалог открывается и из карточки мерчанта: она обязана показать
      // новые значения сразу, а не после ухода на список и обратно.
      if (dialog.kind === 'edit' && this.cardId === dialog.account.id) await this.card.load()
      runInAction(() => { this.dialog = { kind: 'none' } })
    } catch (cause) {
      this.ui.failure(explain(cause))
    } finally {
      runInAction(() => { this.busy = false })
    }
  }

  async remove(): Promise<void> {
    const dialog = this.dialog
    if (dialog.kind !== 'remove' || this.busy) return
    this.busy = true
    try {
      await api.admin.deleteAccount(dialog.account.id)
      await this.accounts.load()
      runInAction(() => { this.dialog = { kind: 'none' } })
      this.ui.success('Аккаунт удалён')
    } catch (cause) {
      this.ui.failure(explain(cause))
    } finally {
      runInAction(() => { this.busy = false })
    }
  }

  async resetTwoFactor(): Promise<void> {
    const dialog = this.dialog
    if (dialog.kind !== 'reset2fa' || this.busy) return
    this.busy = true
    try {
      await api.admin.resetTwoFactor(dialog.account.id)
      await this.accounts.load()
      runInAction(() => { this.dialog = { kind: 'none' } })
      this.ui.success('Второй фактор сброшен')
    } catch (cause) {
      this.ui.failure(explain(cause))
    } finally {
      runInAction(() => { this.busy = false })
    }
  }

  // ---------------- очередь выводов ----------------

  async approve(withdrawal: WithdrawalAdminResponse): Promise<void> {
    if (this.decidingId !== null) return
    this.decidingId = withdrawal.id
    try {
      await api.admin.approveWithdrawal(withdrawal.id)
      this.ui.success('Заявка одобрена')
    } catch (cause) {
      this.ui.failure(explain(cause))
    } finally {
      // Список перечитывается в любом случае, включая отказ: заявку
      // мог одобрить второй администратор секунду назад, и показывать
      // её как ждущую — значит звать нажать «одобрить» ещё раз.
      await this.pending.load()
      runInAction(() => { this.decidingId = null })
    }
  }

  async reject(withdrawal: WithdrawalAdminResponse, reason: string): Promise<void> {
    if (this.decidingId !== null) return
    this.decidingId = withdrawal.id
    try {
      await api.admin.rejectWithdrawal(withdrawal.id, reason.trim())
      this.ui.success('Заявка отклонена')
    } catch (cause) {
      this.ui.failure(explain(cause))
    } finally {
      await this.pending.load()
      runInAction(() => { this.decidingId = null })
    }
  }
}

function telegramChanged(account: AdminAccountResponse, draft: string): boolean {
  const before = account.telegram_id ? String(account.telegram_id) : ''
  return before !== draft.trim()
}
